/**
 * 3NODE — doplnění údajů firmy z ARES podle IČO v registračním formuláři
 * velkoobchodu (SparkLayer <spark-form>, Freelo 32792715).
 *
 * Formulář je React v otevřeném shadow DOM. Textová pole se zapisují přes
 * nativní setter + událost `input`, jinak by je React při dalším vykreslení
 * zahodil. Výběr země se nastavuje přímo přes `onChange` komponenty (najde se
 * v React fiberu), aby se rozbalovací seznam na obrazovce neotvíral a neblikal.
 * Vyplňují se jen prázdná pole — co zákazník napsal sám, se nepřepisuje.
 * ARES (ares.gov.cz) povoluje volání z prohlížeče (Access-Control-Allow-Origin: *).
 * Zahraniční IČO v ARES nejsou; formulář pak zůstane, jak je.
 */
(() => {
  const ARES = 'https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/';
  const DEBOUNCE = 600; // ms po posledním úhozu — nehledat uprostřed psaní
  const lang = (document.documentElement.lang || 'cs').slice(0, 2);
  const MSG = {
    cs: { loading: 'Hledám firmu v ARES…', ok: 'Údaje firmy jsme doplnili z ARES — zkontrolujte je prosím.', missing: 'IČO jsme v ARES nenašli, vyplňte prosím údaje ručně.' },
    en: { loading: 'Looking up the company…', ok: 'Company details filled in from the Czech business register — please check them.', missing: 'We could not find this ID in the Czech business register. Please fill in the details manually.' },
    de: { loading: 'Firma wird gesucht…', ok: 'Firmendaten aus dem tschechischen Handelsregister ergänzt — bitte prüfen.', missing: 'Diese ID ist im tschechischen Handelsregister nicht zu finden. Bitte Daten manuell ausfüllen.' },
  };
  const t = MSG[lang] || MSG.en;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const frame = () => new Promise((r) => requestAnimationFrame(() => r()));

  // Kontrolní číslice IČO (modulo 11) — na překlepy se ARES vůbec neptáme.
  const validIco = (ico) => {
    if (!/^\d{8}$/.test(ico)) return false;
    let sum = 0;
    for (let i = 0; i < 7; i++) sum += Number(ico[i]) * (8 - i);
    return (11 - (sum % 11)) % 10 === Number(ico[7]);
  };

  // Jednou do shadow DOM: jemné zvýraznění doplněných polí a stav pod IČO.
  const ensureStyle = (root) => {
    if (root.getElementById('rf-ares-style')) return;
    const s = document.createElement('style');
    s.id = 'rf-ares-style';
    s.textContent = `
      @keyframes rf-ares-fill { from { background-color: #fff1e8; } to { background-color: transparent; } }
      .rf-ares-filled { animation: rf-ares-fill 1.6s ease-out; }
      #rf-ares-status { margin: 6px 0 0; font-size: 13px; line-height: 1.4; color: #555; }
    `;
    root.appendChild(s);
  };

  const setValue = (el, value) => {
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
    setter.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    el.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    el.classList.remove('rf-ares-filled');
    void el.offsetWidth; // restart animace
    el.classList.add('rf-ares-filled');
  };

  const fillEmpty = (root, name, value) => {
    const el = root.querySelector(`input[name="${name}"]`);
    if (el && value && !el.value.trim()) setValue(el, String(value));
  };

  // Výběr země: komponenta s `allowedCountries` nad tlačítkem má onChange('CZ').
  const setCountry = (root, buttonId, code) => {
    const btn = root.getElementById(buttonId);
    if (!btn) return false;
    const key = Object.keys(btn).find((k) => k.startsWith('__reactFiber'));
    let f = key && btn[key];
    while (f && !(f.memoizedProps && 'allowedCountries' in f.memoizedProps)) f = f.return;
    const props = f && f.memoizedProps;
    if (!props || typeof props.onChange !== 'function') return false;
    if (!props.value) props.onChange(code);
    return true;
  };

  const status = (root, input, text) => {
    let el = root.getElementById('rf-ares-status');
    if (!el) {
      el = document.createElement('p');
      el.id = 'rf-ares-status';
      el.setAttribute('role', 'status');
      input.insertAdjacentElement('afterend', el);
    }
    el.textContent = text;
  };

  const address = (s) => {
    const street = s.nazevUlice || s.nazevCastiObce || s.nazevObce || '';
    let number = s.cisloDomovni ? String(s.cisloDomovni) : '';
    if (s.cisloOrientacni) number += `/${s.cisloOrientacni}${s.cisloOrientacniPismeno || ''}`;
    const city = s.nazevObce === 'Praha' && s.nazevMestskehoObvodu ? s.nazevMestskehoObvodu : s.nazevObce;
    const psc = s.psc ? String(s.psc).replace(/^(\d{3})(\d{2})$/, '$1 $2') : '';
    return { line1: `${street} ${number}`.trim(), city, psc };
  };

  // Rozbalení formuláře (ruční adresa, pole DIČ) posune obsah; pole IČO
  // držíme na místě, aby stránka neposkočila pod rukama.
  const keepInPlace = async (input, change) => {
    const before = input.getBoundingClientRect().top;
    await change();
    await frame();
    const after = input.isConnected ? input.getBoundingClientRect().top : before;
    // Téma má scroll-behavior: smooth — korekce musí být okamžitá, ne animovaná.
    if (Math.abs(after - before) > 1) window.scrollBy({ top: after - before, behavior: 'instant' });
  };

  let lastIco = '';
  let timer = null;

  const lookup = async (root, input) => {
    const ico = input.value.replace(/\s/g, '');
    if (ico === lastIco || !validIco(ico)) return;
    lastIco = ico;
    ensureStyle(root);

    // „Hledám…" jen když ARES chvíli trvá — jinak by text jen probliknul.
    const slow = setTimeout(() => status(root, input, t.loading), 400);
    let d;
    try {
      const res = await fetch(ARES + ico, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(res.status);
      d = await res.json();
    } catch (e) {
      clearTimeout(slow);
      status(root, input, t.missing);
      return;
    }
    clearTimeout(slow);
    if (ico !== input.value.replace(/\s/g, '')) return; // mezitím přepsáno

    await keepInPlace(input, async () => {
      fillEmpty(root, 'company_name', d.obchodniJmeno);
      setCountry(root, 'field-country', 'CZ');

      // Ruční pole adresy se objeví až po „Zadat adresu ručně" — jen když
      // zákazník nezačal adresu hledat sám.
      if (!root.querySelector('input[name="address_city"]')) {
        const lookupField = root.getElementById('field-address-lookup');
        const manual = [...root.querySelectorAll('button')].find((b) => /ručně|manually|manuell/i.test(b.textContent));
        if (manual && !(lookupField && lookupField.value.trim())) {
          manual.click();
          await frame();
          await sleep(50);
        }
      }
      const a = address(d.sidlo || {});
      fillEmpty(root, 'address_company', d.obchodniJmeno);
      fillEmpty(root, 'address_address_line1', a.line1);
      fillEmpty(root, 'address_city', a.city);
      fillEmpty(root, 'address_postal_code', a.psc);

      if (d.dic && setCountry(root, 'field-tax_country', 'CZ')) {
        for (let i = 0; i < 20 && !root.querySelector('input[name="tax_tax_id"]'); i++) await sleep(50);
        fillEmpty(root, 'tax_tax_id', d.dic);
      }
    });

    const icoNow = root.querySelector('input[name="ico"]') || input;
    status(root, icoNow, t.ok);
    if (root.activeElement !== icoNow) icoNow.focus({ preventScroll: true });
  };

  // Formulář se načítá líně (až když na něj zákazník doscrolluje), proto se
  // nečeká na jeho vykreslení a poslouchá se na dokumentu: nativní `input` je
  // composed, skutečné pole je první prvek v composedPath().
  document.addEventListener('input', (e) => {
    if (!e.isTrusted) return;
    const input = e.composedPath()[0];
    if (!(input instanceof HTMLInputElement) || input.name !== 'ico') return;
    const root = input.getRootNode();
    if (!(root instanceof ShadowRoot) || root.host.tagName !== 'SPARK-FORM') return;
    clearTimeout(timer);
    timer = setTimeout(() => lookup(root, input), DEBOUNCE);
  });
})();
